import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { qtyReceived, qtyOutstanding } from "../lib/receiving";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const LineRow = ({ l, po, value, onChange }) => {
  const out = qtyOutstanding(l, po);
  return (
    <tr className="border-b border-border/60" data-testid="receive-line">
      <td className="py-1.5 pr-2 text-sm">{l.description}</td>
      <td className="py-1.5 px-2 text-right tabular text-sm">{Number(l.quantity || 0)}</td>
      <td className="py-1.5 px-2 text-right tabular text-sm text-muted-foreground">{qtyReceived(l, po)}</td>
      <td className="py-1.5 px-2 text-right tabular text-sm font-medium" data-testid="receive-line-outstanding">{out}</td>
      <td className="py-1.5 pl-2 w-28">
        <Input type="number" min={0} max={out} step="any" disabled={out <= 0} value={value}
          onChange={(e) => onChange(e.target.value)} className="h-8 rounded-sm text-right num" data-testid="receive-line-qty" />
      </td>
    </tr>
  );
};

export const ReceiveDialog = ({ open, onOpenChange, po, onDone }) => {
  const [qty, setQty] = useState({});
  const [by, setBy] = useState("");
  const [notify, setNotify] = useState("");
  const lines = po?.lines || [];
  useEffect(() => {
    if (open && po) {
      setQty(Object.fromEntries(lines.map((l) => [l.line_id, String(qtyOutstanding(l, po))])));
      setBy(po.received_by || "");
      setNotify(po.arrival_notify || "");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const negative = lines.some((l) => Number(qty[l.line_id] || 0) < 0);
  const over = negative || lines.some((l) => Number(qty[l.line_id] || 0) > qtyOutstanding(l, po) + 1e-6);
  const totalQty = lines.reduce((s, l) => s + Number(qty[l.line_id] || 0), 0);
  const post = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${po.id}/post-receipt`, {
      lines: lines.map((l) => ({ line_id: l.line_id, quantity: Number(qty[l.line_id] || 0) })), received_by: by, notify_emails: notify,
    })).data,
    onSuccess: (r) => {
      toast.success(`${r.receipt.receipt_no} posted · ${r.fully_received ? "order fully received" : "rest stays open for later deliveries"}${r.alert_to?.length ? ` · arrival email to ${r.alert_to.join(", ")}` : ""}`);
      onOpenChange(false);
      onDone?.(r);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-sm" data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle>Receive {po?.doc_no}</DialogTitle>
          <DialogDescription>Enter what actually arrived. Anything not received stays open on the order.</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="text-left border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-2 pr-2 font-semibold">Item</th><th className="py-2 px-2 font-semibold text-right">Ordered</th>
              <th className="py-2 px-2 font-semibold text-right">Received</th><th className="py-2 px-2 font-semibold text-right">Outstanding</th>
              <th className="py-2 pl-2 font-semibold text-right">Receive now</th>
            </tr></thead>
            <tbody>{lines.map((l) => <LineRow key={l.line_id} l={l} po={po} value={qty[l.line_id] ?? ""} onChange={(v) => setQty((q) => ({ ...q, [l.line_id]: v }))} />)}</tbody>
          </table>
        </div>
        {over && <p className="text-xs text-destructive" data-testid="receive-over-error">{negative ? "Quantities can't be negative." : "You can't receive more than is still outstanding on a line."}</p>}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Received by</label>
            <Input value={by} onChange={(e) => setBy(e.target.value)} placeholder="Who checked the goods in" className="rounded-sm h-9" data-testid="receive-received-by" />
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setQty(Object.fromEntries(lines.map((l) => [l.line_id, "0"])))} data-testid="receive-clear">Clear all</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setQty(Object.fromEntries(lines.map((l) => [l.line_id, String(qtyOutstanding(l, po))])))} data-testid="receive-fill">Fill outstanding</Button>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Also notify (arrival email)</label>
          <Input value={notify} onChange={(e) => setNotify(e.target.value)} placeholder="other@company.com, …" className="rounded-sm h-9" data-testid="receive-notify-emails" />
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="receive-notify-hint">{po?.created_by_name ? `${po.created_by_name}, who raised this order,` : "The person who raised this order"} is emailed automatically with the receipt attached.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm" disabled={over || totalQty <= 0 || post.isPending} onClick={() => post.mutate()} data-testid="receive-confirm">Post Receipt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
